import { useEffect, useMemo, useState } from 'react'
import { client } from '../lib/sanity'
import useDebouncedPatchQueue from './useDebouncedPatchQueue'
import useUndoQueue from './useUndoQueue'

const nowISO = () => new Date().toISOString()

const NOTE_FIELDS = `_id, _type, kind, title, content, tags, archived, archivedAt, notes, pinned, done, dueAt, priority, order, createdAt, updatedAt`

const QUICKTODO_ARCHIVE_LIMIT = 20

export default function useNotes() {
    const [notes, setNotes] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [savingIds, setSavingIds] = useState(new Set())
    const enqueuePatch = useDebouncedPatchQueue(800)

    useEffect(() => {
        ;(async () => {
            try {
                setLoading(true)
                const groq = `*[_type == "note"] | order(pinned desc, updatedAt desc, createdAt desc)[0...200]{${NOTE_FIELDS}}`
                const data = await client.fetch(groq)
                setNotes(Array.isArray(data) ? data : [])
            } catch (e) {
                console.error(e)
                setError('Failed to load notes.')
            } finally {
                setLoading(false)
            }
        })()
    }, [])

    const markSaving = (id, on) => {
        setSavingIds((prev) => {
            const next = new Set(prev)
            if (on) next.add(id)
            else next.delete(id)
            return next
        })
    }

    const createNote = async (kind = 'note') => {
        const base = { _type: 'note', kind, title: '', pinned: false, createdAt: nowISO(), updatedAt: nowISO() }
        const doc =
            kind === 'task'
                ? { ...base, notes: '', priority: 'medium', done: false, dueAt: null }
                : { ...base, content: '', tags: [], archived: false }
        try {
            const created = await client.create(doc)
            const full = { ...doc, _id: created._id }
            setNotes((ns) => [full, ...ns])
            return full
        } catch (e) {
            console.error(e)
            setError("Couldn't create note.")
            return null
        }
    }

    const patchNote = (id, patch) => {
        setNotes((ns) => ns.map((n) => (n._id === id ? { ...n, ...patch, updatedAt: nowISO() } : n)))
        markSaving(id, true)
        enqueuePatch(id, patch, async (merged) => {
            try {
                await client.patch(id).set({ ...merged, updatedAt: nowISO() }).commit()
            } catch (e) {
                console.error(e)
                setError('Save failed. Changes kept locally; try again.')
            } finally {
                markSaving(id, false)
            }
        })
    }

    const createQuickTodo = async (title, order) => {
        const doc = {
            _type: 'note',
            kind: 'quicktodo',
            title: title.trim(),
            done: false,
            archived: false,
            order,
            pinned: false,
            createdAt: nowISO(),
            updatedAt: nowISO(),
        }
        try {
            const created = await client.create(doc)
            const full = { ...doc, _id: created._id }
            setNotes((ns) => [...ns, full])
            return full
        } catch (e) {
            console.error(e)
            setError("Couldn't create item.")
            return null
        }
    }

    const archiveQuickTodo = (id) => {
        const archivedAt = nowISO()
        patchNote(id, { archived: true, archivedAt, done: true })
        setNotes((ns) => {
            const archivedQuickTodos = ns.filter((n) => n.kind === 'quicktodo' && n.archived)
            if (archivedQuickTodos.length <= QUICKTODO_ARCHIVE_LIMIT) return ns
            const oldest = archivedQuickTodos.reduce((a, b) =>
                new Date(a.archivedAt || 0) <= new Date(b.archivedAt || 0) ? a : b
            )
            client.delete(oldest._id).catch((e) => {
                console.error(e)
                setError('Failed to evict oldest archived item.')
            })
            return ns.filter((n) => n._id !== oldest._id)
        })
    }

    const deleteQuickTodo = (id) => {
        setNotes((ns) => ns.filter((n) => n._id !== id))
        client.delete(id).catch((e) => {
            console.error(e)
            setError('Delete failed.')
        })
    }

    const restoreNote = (note) => setNotes((ns) => [note, ...ns])

    const undoQueue = useUndoQueue({
        delay: 5000,
        onExpire: async (note) => {
            await client.delete(note._id)
        },
        onRestore: (note, err) => {
            if (err) setError('Delete failed. Restoring note.')
            restoreNote(note)
        },
    })

    const requestDelete = (note) => {
        setNotes((ns) => ns.filter((n) => n._id !== note._id))
        undoQueue.requestDelete(note)
    }

    const importLines = async (lines) => {
        if (lines.length === 0) return { succeeded: 0, failed: [] }
        const now = nowISO()
        const payloads = lines.map((line) => ({
            _type: 'note',
            kind: 'note',
            title: line.slice(0, 120),
            content: '',
            tags: [],
            archived: false,
            pinned: false,
            createdAt: now,
            updatedAt: now,
        }))
        const results = await Promise.allSettled(payloads.map((doc) => client.create(doc)))
        const succeededDocs = []
        const failed = []
        results.forEach((r, i) => {
            if (r.status === 'fulfilled') succeededDocs.push({ ...payloads[i], _id: r.value._id })
            else failed.push({ line: lines[i], reason: r.reason?.message })
        })
        if (succeededDocs.length) setNotes((ns) => [...succeededDocs, ...ns])
        if (failed.length) setError(`${failed.length} of ${lines.length} import line(s) failed — the rest were created.`)
        return { succeeded: succeededDocs.length, failed }
    }

    const bulkPatch = async (ids, fields) => {
        if (ids.length === 0) return
        try {
            const tx = client.transaction()
            ids.forEach((id) => tx.patch(id, { set: { ...fields, updatedAt: nowISO() } }))
            await tx.commit()
            setNotes((ns) => ns.map((n) => (ids.includes(n._id) ? { ...n, ...fields, updatedAt: nowISO() } : n)))
        } catch (e) {
            console.error(e)
            setError('Bulk update failed.')
        }
    }

    const allTags = useMemo(() => {
        const seen = new Map()
        notes.forEach((n) =>
            (n.tags || []).forEach((t) => {
                const key = t.toLowerCase()
                if (!seen.has(key)) seen.set(key, t)
            })
        )
        return Array.from(seen.values()).sort((a, b) => a.localeCompare(b))
    }, [notes])

    return {
        notes,
        setNotes,
        loading,
        error,
        setError,
        savingIds,
        createNote,
        patchNote,
        requestDelete,
        bulkPatch,
        createQuickTodo,
        archiveQuickTodo,
        deleteQuickTodo,
        undoNote: undoQueue.undo,
        undoAllNotes: undoQueue.undoAll,
        pendingDeletes: undoQueue.pending,
        importLines,
        allTags,
    }
}
